<template>
  <div class="day" :style="dayStyle">
    <div class="day__header">
      <template v-if="MENU_MODE">
        <div class="day__name">
          {{ day.date | format_date }} <br>
          {{ day.date | format_date('dddd') | capitalize }}
        </div>
      </template>
      <template v-else>
        <div class="day__name">
          {{ 'app.day' | trans }} {{ getDayIndexById(day.id) + 1 }}
        </div>
      </template>
      <dropdown class="dropdown-actions" v-show="somePermission('creator_plan_add')" :position="isFirst?'left':'right'">
        <li role="presentation" v-if="hasPermission('creator_plan_add')">
          <a @click="showModal({ component: 'add-plan', data: {dayId: day.id}, options:
                    {large: false} })">
            <i class="fa fa-plus-circle" aria-hidden="true"></i>Dodaj posiłek
          </a>
        </li>
        <li role="presentation" class="divider"></li>
        <slot name="actions"></slot>
      </dropdown>
      <div :class="{hidden: this.commentsHidden }" class="day__comment">
                <span @click="getDayCommentRemoveClickListener()"
                      :class="{hidden: !day.hasComment()}"
                      class="day__comment_button"
                      role="button"
                      title="Usuń komentarz">
                <i class="fa fa-times text-red"></i>
                </span>
        <span @click="getDayCommentAddClickListener()"
              :class="{hidden: day.hasComment()}"
              class="day__comment_button"
              role="button"
              ref="addDayComment"
              title="Dodaj komentarz"
        >
                <i class="fa fa-commenting text-success"></i>
                </span>
        <p class="day__comment_input" contenteditable="true"
           ref="dayComment"
           title="Kliknij by edytować"
           placeholder="Komentarz..."
           :style="{visibility: dayCommentInputVisibility}"
           v-comment-input-click-outside="{
                    exclude: ['dayComment', 'addDayComment'],
                    handler: 'getDayCommentOnOutsideClickListener',
               }"
           @blur="getDayCommentOnBlurListener($event)"
           v-html="day.comment"
        >
        </p>
      </div>
      <hr>
      <div class="text-center">
        <div class="day__summary" :class="indicatorClass(sumEnergy, prescribedEnergy)">
          <strong v-tooltip="'Sumaryczna energia posiłków'" class="indicator">
            {{ sumEnergy | integers }}
          </strong>/
          <strong v-tooltip="'Założeniowa energia posiłków dnia'"
                  class="text-blue">{{ prescribedEnergy }}
          </strong>
          kcal
        </div>
      </div>
    </div>
    <hr>
    <plan v-for="plan in plans" :key="plan.id" :planId="plan.id" :dayId="day.id"
          :is-assigned-to-user="isAssignedToUser" :is-first-day="isFirst"></plan>

    <div class="nutrients__summary">
      <table class="mb-20">
        <tr v-for="(nutrient, i) in nutrientsSummary" :key="i" v-show="i < 4 || showNutrientsDetails"
            :class="indicatorClass(nutrient.total, nutrient.amount)" v-tooltip="nutrient.name"
            v-if="nutrient.externalId !== 156">
          <td :colspan="[nutrient.amount === 0 ? 2 : '']">
            <span class="nutrient__name text-bold">{{ nutrient.shortName }}</span>
            <span v-if="!mainNutrients.includes(nutrient.externalId)">{{ nutrient.total | round }}</span>
            <span v-if="mainNutrients.includes(nutrient.externalId)">{{ nutrient.total | integers }}</span>
            <span v-if="nutrient.amount !== 0">/ {{ nutrient.amount | round }}</span> {{ nutrient.unit }}
          </td>
          <td class="indicator" v-if="nutrient.amount !== 0">
            <strong>
              {{ calculatePercentage(nutrient.total, nutrient.amount) | integers }}%
            </strong>
          </td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import {mapGetters, mapMutations} from 'vuex';
import {CARBO, FATS, KCAL, NutrientDetailsProvider, PROTEIN} from "../services/NutrientDetailsProvider";
import {round} from "../utils/money";

export default {
    props: {
        id: {
            type: String,
            required: true,
        },
        showNutrientsDetails: {
            type: Boolean,
        },
        isAssignedToUser: {
            type: Boolean
        },
        isFirst: {
            type: Boolean,
        },
    },
  data() {
    return {
      dayCommentInputVisibility: 'hidden',
    dayWidth: 280,}
  },
  mounted() {
    this.toggleComment = ((allCommentHidden) => {
      if (!allCommentHidden && this.day.hasComment()) {
        this.showDayCommentInput();
      }
    });
    this.removeComment = (() => {
      this.day.comment = '';
      this.hideDayCommentInput();
    });
    this.$root.$on('remove_all_comments', this.removeComment);
    this.$root.$on('toggle_all_comments', this.toggleComment);
  this.setDayWidth();},
  created() {
    if (!this.commentsHidden && this.day.hasComment()) {
      this.showDayCommentInput();
    }window.addEventListener('resize', this.setDayWidth);
  },
  beforeDestroy() {
    this.$root.$off('toggle_all_comments', this.toggleComment);
    this.$root.$off('remove_all_comments', this.removeComment);
  },
  methods: {
    ...mapMutations(['showModal']),
    calculatePercentage(given, wanted) {
      return (given / wanted) * 100;
    },
    indicatorClass(given, wanted) {
      const percentage = this.calculatePercentage(given, wanted);

      if (percentage > 110) {
        return 'nutrients--red';
      }

      if (percentage <= 110 && percentage >= 90) {
        return 'nutrients--green';
      }

      return 'nutrients--primary';
    },

    hideDayCommentInput() {
      this.dayCommentInputVisibility = 'hidden';
    },

    showDayCommentInput() {
      this.dayCommentInputVisibility = 'visible';
    },

    getDayCommentOnBlurListener($event) {
      this.day.comment = this.$sanitize($event.target.innerText).replace(/\n/g, '<br>');
    },

    getDayCommentOnOutsideClickListener() {
      if (!this.day.hasComment()) {
        this.hideDayCommentInput();
      }
    },
    getDayCommentRemoveClickListener() {
      if (this.day.hasComment()) {
        this.$dialog.confirm('Czy na pewno chcesz usunąć ten komentarz?').then(() => {
          this.day.comment = '';
          this.hideDayCommentInput();
        }).catch(() => {
        });
      }
    },
    getDayCommentAddClickListener() {
      this.showDayCommentInput();
      this.$nextTick(() => {
        this.$refs.dayComment.focus();
      });
    },
    setDayWidth() {
      const menuWindow = document.querySelector('.menu__window');
      const daysCount = document.querySelectorAll('.day').length;
      if (menuWindow === null || daysCount === 0) {
        this.dayWidth = 280;
        return;
      }
      const divider = daysCount > 7 ? 7 : daysCount;
      let width = round((menuWindow.offsetWidth - 40) / divider, 0);
      if (width < 150) {
        width = 150;
      } else if (width > 300) {
        width = 300;
      }
      this.dayWidth = width;
    }
  },
  computed: {
    ...mapGetters([
      'getNutrientsByDay',
      'getDayById',
      'MENU_MODE',
      'getDayIndexById',
      'hasPermission',
      'somePermission',
      'commentsHidden',
      'MENU_PRESCRIBED_NUTRIENTS',
    ]),
    mainNutrients: () => [PROTEIN, FATS, CARBO, KCAL],
    day() {
      return this.getDayById(this.id);
    },
    plans() {
      return this.day.plans.sort((a, b) => a.date - b.date);
    },
    sumEnergy() {
      return this.day.plans
          .map((plan) => plan.totalEnergy.amount)
          .reduce((total, previous) => total + previous, 0);
    },
    prescribedEnergy() {
      return this.plans
          .map(p => Number(p.prescribedEnergy.amount))
          .reduce((total, previous) => total + previous, 0);
    },
    nutrientsSummary() {
      const nutrientsByDay = this.getNutrientsByDay(this.day.id);
      let summary = [];
      for (const externalId in nutrientsByDay) {
        const nutrientDetails = NutrientDetailsProvider.getById(externalId);
        if (typeof nutrientDetails !== 'undefined') {
          summary.push({
            name: nutrientDetails.name,
            total: nutrientsByDay[externalId],
            shortName: nutrientDetails.short_name,
            unit: nutrientDetails.unit,
            amount: this.MENU_PRESCRIBED_NUTRIENTS[externalId],
            externalId: Number(externalId),
            position: nutrientDetails.position,
            groupId: nutrientDetails.group_id,
          });
        }
      }
      return NutrientDetailsProvider.sortNutrients(summary);
    },
    dayStyle() {
      return `width: ${this.dayWidth}px;`
    }
  },
};
</script>

<style lang="scss" type="text/scss" scoped>

.day {
  padding: 5px 4px;
  background: #eee;
  margin: 2px;
  border-radius: 3px;

  border: 1px solid #d2d2d2;
}

.nutrients__summary {
  align-self: flex-end;
  margin-top: auto;

  table {

    border: 0;
    border-collapse: collapse;
    width: 100%;
    font-size: 11px;
    table-layout: fixed;

    td {
      transition: .3s background-color;
      border-bottom: 1px solid #d8d8d8;
    }

    tr {
      span.nutrient__name {
        position: absolute;
        top: 2px;
        left: 2px;
        font-style: italic;
      }

      td:first-child {
        position: relative;
      }

      td {
        white-space: nowrap;
        padding: 17px 2px 2px;
        width: 50%;
        text-align: right;
      }

      td:nth-child(2) {
        width: 50px;
      }

      &:hover td {
        background-color: whitesmoke;
      }
    }
  }
}

.day__header {
  text-align: center;
  font-weight: bold;
  position: relative;
  font-size: 15px;
  line-height: 16px;
  display: block;

  .day__name {
    min-height: 32px;
    vertical-align: middle;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.day__summary {
  font-size: 15px;
}

hr {
  margin-top: 7px;
  margin-bottom: 7px;
  border-color: #d2d2d2;
}

.day__comment {
  position: relative;
}

.day__comment_input:focus {
  background: #fff;
}

.day__comment_input:hover:not(:focus) {
  cursor: pointer;
}

.day__comment_input:hover {
  cursor: auto;
}

.day__comment_input:empty:before {
  content: attr(placeholder);
  pointer-events: none;
  display: block;
}
</style>
